import { OutboxMessageCriteria } from '@src/main/modules/messages/domain/message/outbox/outbox-message-criteria.interface';
import { OutboxMessage } from '@src/main/modules/messages/domain/message/outbox/outbox-message.interface';
import { OutboxMessageRepository } from '@src/main/modules/messages/domain/message/outbox/outbox-message.repository';
import {
  CreateMessageRequest,
  toFormData,
} from '@src/main/modules/messages/infrastructure/dto/createMessageRequest';
import { OutboxMessageEntity } from '@src/main/modules/messages/infrastructure/entity/outbox-message.entity';
import { parseOutboxMessageCriteriaWithDistinct } from '@src/main/modules/messages/infrastructure/mapper/message-criteria.mapper';
import { toOutboxMessage } from '@src/main/modules/messages/infrastructure/mapper/message.mapper';
import { api, TokenProvider } from '@src/main/shared/infrastructure/entity/axios';
import { PageResponse } from '@src/main/shared/infrastructure/entity/page.entity';

const path = '/notifications';

export const createOutboxMessageHttpRepository = (
  tokenProvider: TokenProvider,
): OutboxMessageRepository => new OutboxMessageHttpRepository(tokenProvider);

class OutboxMessageHttpRepository implements OutboxMessageRepository {
  private readonly tokenProvider: TokenProvider;

  constructor(tokenProvider: TokenProvider) {
    this.tokenProvider = tokenProvider;
  }

  private async getAuthToken(): Promise<string> {
    const token = await this.tokenProvider.getToken();
    if (!token) throw new Error('Authentication token is missing');
    return token;
  }

  async findByCriteria(criteria: OutboxMessageCriteria): Promise<PageResponse<OutboxMessage>> {
    const token = await this.getAuthToken();
    const params = parseOutboxMessageCriteriaWithDistinct(criteria);
    const response = await api.get<PageResponse<OutboxMessageEntity>>(`${path}`, token, params);
    return {
      ...response,
      items: response.items.map(toOutboxMessage),
    };
  }

  async findById(id: string): Promise<OutboxMessage> {
    const token = await this.getAuthToken();
    const entity = await api.get<OutboxMessageEntity>(`${path}/${id}`, token);
    return toOutboxMessage(entity);
  }

  async findBySendBy(id: string): Promise<OutboxMessage> {
    const token = await this.getAuthToken();
    const entity = await api.get<OutboxMessageEntity>(`${path}/send_by/${id}`, token);
    return toOutboxMessage(entity);
  }

  async create(message: CreateMessageRequest): Promise<OutboxMessage> {
    const token = await this.getAuthToken();
    const formData = toFormData(message);
    const entity = await api.post<OutboxMessageEntity>(path, token, formData, true);
    return toOutboxMessage(entity);
  }
}
